import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { FaBan, FaCheckCircle, FaEdit, FaParking, FaUserCircle } from 'react-icons/fa';
import { MdOutlineClosedCaptionDisabled } from 'react-icons/md';
import { driverConfirm, getBooking, removeImageDriverTran, updateDriverTran } from 'redux/booking/car/carBookingSlice';
import UploadDocument from 'components/common/upload/UploadDocument';
import DocumentAttachment from 'components/common/upload/DocumentAttachment';
import { formatNumber } from 'helpers/utils';
import { Card, Badge, Alert, Button } from 'components/common';
import TransactionDetailBookingInfo from './detail/TransactionDetailBookingInfo';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const labelClass = 'tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5';
const errClass = 'tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0';

const Line = ({ label, children, strong = false }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className={`sm:tw-w-32 sm:tw-flex-shrink-0 ${strong ? 'tw-text-blue-700 tw-font-semibold' : 'tw-text-slate-600'}`}>{label}</span>
    <span className="tw-text-blue-700 tw-min-w-0 tw-break-words">{children}</span>
  </div>
);
Line.propTypes = { label: PropTypes.node, children: PropTypes.node, strong: PropTypes.bool };

const SectionTitle = ({ children }) => (
  <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">{children}</h6>
);
SectionTitle.propTypes = { children: PropTypes.node };

const TransactionDriverUpdateDeparture = () => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const { recordData: transaction } = useSelector((state) => state.booking_car);
  const dispatch = useDispatch();
  const params = useParams();
  const transactionId = params.id;
  const driverTranId = params.driver_tran_id;
  const driverTrans = transaction.driver_trans || [];

  const driver_tran = driverTrans.filter((item) => item.id === parseInt(driverTranId, 10))[0] || {};
  const [showFormUpdate, setShowFormUpdate] = useState(!driver_tran.departure_begin_kilometer);
  const [images, setImages] = useState([]);
  const fileTypes = 'image/*';
  const fileNote = 'Only image/* files type are allowed. The file size is maximum of 20Mb';

  const { allow_edit, allow_remove_image } = driver_tran?.permissions || {};

  useEffect(() => {
    dispatch(getBooking(transactionId));
  }, [params, dispatch, transactionId]);

  const dateConvert = (date) => new Date(date);

  useEffect(() => {
    if (driver_tran && showFormUpdate) {
      setValue('departure_begin_time', dateConvert(driver_tran.departure_begin_time || transaction.from_date));
      setValue('departure_end_time', dateConvert(driver_tran.departure_end_time || transaction.to_date));
      setValue('departure_begin_kilometer', driver_tran.departure_begin_kilometer);
      setValue('departure_end_kilometer', driver_tran.departure_end_kilometer);
      setValue('notes', driver_tran.notes);
      setValue('parking_fee', driver_tran.parking_fee);
    } else {
      setValue('departure_begin_time', dateConvert(transaction.from_date));
      setValue('departure_end_time', dateConvert(transaction.to_date));
    }
    // seed only when the driver tran/form toggle changes; re-running when getBooking refreshes the transaction would overwrite the user's edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, showFormUpdate, driver_tran]);

  const closeUpdateForm = () => setShowFormUpdate(false);

  const onSubmit = (formData) => {
    const data = { ...formData, images, closed: true }; // lock deny user update again!
    try {
      if (driver_tran) {
        dispatch(updateDriverTran({ transactionId, driverTranId, data }));
        closeUpdateForm();
      } else {
        toast.error('Error update driver.');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const validateDepartureKilometers = (beginKm, endKm) => {
    if (beginKm <= endKm) return true;
    toast.error('Departure end (km) must be greater than Departure begin (km)');
    return true;
  };

  const handleConfirm = () => dispatch(driverConfirm({ transactionId, driverTranId }));

  const formatDate = (date) => (date ? format(new Date(date), 'MMM, dd-yyyy h:mm aa') : '');

  const removeDriverTranDocument = (imageId) => {
    try {
      dispatch(removeImageDriverTran({ transactionId, driverTranId, imageId }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const driverConfirmed = driver_tran && driver_tran.confirmed;

  return (
    <div className="tw-p-4">
      {!driverConfirmed && (
        <Alert type="warning" className="tw-mb-3">Waiting for confirmation by driver!</Alert>
      )}

      {showFormUpdate ? (
        <Card
          padding="none"
          className="tw-mb-3"
          footer={(
            <div className="tw-flex tw-justify-center tw-gap-2">
              <Button size="sm" type="submit" form="driver-departure-form" variant="primary" leftIcon={MdOutlineClosedCaptionDisabled}>
                Update and send to Admin
              </Button>
              <Button size="sm" variant="secondary" leftIcon={FaBan} onClick={closeUpdateForm}>Cancel</Button>
            </div>
          )}
        >
          <form id="driver-departure-form" onSubmit={handleSubmit(onSubmit)} className="tw-p-4">
            <Alert type="info" className="tw-mb-3">
              Specify the departure time and kilometers for the booking. After you update this information, it will be locked and sent to HR Admin.
            </Alert>

            <div className="tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-p-4">
              <div className="tw-mb-4">
                <label className={labelClass}>Departure time</label>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <Controller
                    control={control}
                    name="departure_begin_time"
                    rules={{ required: 'Departure begin time must be required' }}
                    render={({ field }) => (
                      <DatePicker selected={field.value} onChange={(date) => field.onChange(date)} className={inputClass}
                        placeholderText="From" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect showTimeSelectOnly />
                    )}
                  />
                  <Controller
                    control={control}
                    name="departure_end_time"
                    rules={{ required: 'Departure end time must be required' }}
                    render={({ field }) => (
                      <DatePicker selected={field.value} onChange={(date) => field.onChange(date)} className={inputClass}
                        placeholderText="To" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect showTimeSelectOnly />
                    )}
                  />
                </div>
              </div>

              <div className="tw-mb-4">
                <label className={labelClass}>Departure (km)</label>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <div>
                    <input type="number" min={0} placeholder="From" className={inputClass}
                      {...register('departure_begin_kilometer', { required: 'Departure from (km) must be required' })} />
                    {errors.departure_begin_kilometer && <p className={errClass}>{errors.departure_begin_kilometer.message}</p>}
                  </div>
                  <div>
                    <input type="number" min={0} placeholder="To" className={inputClass}
                      {...register('departure_end_kilometer', {
                        required: 'Departure to (km) must be required',
                        validate: (value) => validateDepartureKilometers(Number(getValues('departure_begin_kilometer')), Number(value)),
                      })} />
                    {errors.departure_end_kilometer && <p className={errClass}>{errors.departure_end_kilometer.message}</p>}
                  </div>
                </div>
              </div>

              <div className="tw-mb-4">
                <label className={labelClass}>Parking fee (VND)</label>
                <input type="number" placeholder="Parking fee" className={inputClass} {...register('parking_fee')} />
              </div>

              <div className="tw-mb-4">
                <label className={labelClass}>Notes</label>
                <textarea rows={6} placeholder="Notes" className={inputClass} {...register('notes', { required: 'Notes must be required' })} />
                {errors.notes && <p className={errClass}>{errors.notes.message}</p>}
              </div>

              <div>
                <label className={labelClass}>Attachments</label>
                <UploadDocument files={images} setFiles={setImages} fileTypes={fileTypes} note={fileNote} />
              </div>
            </div>
          </form>
        </Card>
      ) : (
        <Card
          padding="none"
          className="tw-mb-3"
          header={(
            <div className="tw-flex tw-items-center tw-justify-between">
              <h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800">Car and Driver information</h5>
              {allow_edit && <Button size="sm" variant="secondary" leftIcon={FaEdit} onClick={() => setShowFormUpdate(true)}>Edit</Button>}
            </div>
          )}
        >
          <div className="tw-bg-slate-50 tw-p-4">
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-mb-4">
              <div>
                <SectionTitle>Information</SectionTitle>
                <div className="tw-mb-3 tw-text-sm tw-text-slate-700">Booking assigned to driver:</div>
                <Line label="Car number">{driver_tran.car?.name} / <strong>{driver_tran.car?.model}</strong></Line>
                <Line label="Driver name">{driver_tran.driver?.name}</Line>
                <Line label="Email">{driver_tran.driver?.email}</Line>
                <Line label="Phone number">{driver_tran.driver?.phone}</Line>
              </div>
              <div>
                <SectionTitle>Confirmation</SectionTitle>
                {driverConfirmed ? (
                  <>
                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
                      <span className="sm:tw-w-32 sm:tw-flex-shrink-0 tw-text-red-600">Confirmed?</span>
                      <FaCheckCircle className="tw-text-green-600" size={20} />
                    </div>
                    <Line label="Confirmed date">{driver_tran.confirmed_date}</Line>
                    <Line label="Confirmed by"><FaUserCircle className="tw-inline tw-mr-1" /> {driver_tran.confirmed_by}</Line>
                  </>
                ) : (
                  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mt-3 tw-text-sm">
                    <span className="sm:tw-w-32 sm:tw-flex-shrink-0 tw-text-red-600">Confirmed?</span>
                    <div>
                      <Badge variant="warning" size="sm">Waiting for confirmation by driver!</Badge>
                      <div className="tw-mt-1 tw-text-right">
                        Click here to{' '}
                        <button type="button" onClick={handleConfirm}
                          className="tw-bg-transparent tw-border-0 tw-text-blue-600 tw-font-semibold tw-cursor-pointer tw-p-0 hover:tw-underline">
                          Confirm!
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
              <div>
                <SectionTitle>Approval</SectionTitle>
                <Line label="Notes"><span className="tw-text-red-600" dangerouslySetInnerHTML={{ __html: transaction.remark }} /></Line>
                <Line label="Approved by"><FaUserCircle className="tw-inline tw-mr-1" /> {transaction.approved_by}</Line>
                <Line label="Approved date">{transaction.approved_date}</Line>
              </div>
              <div>
                <SectionTitle>Departure</SectionTitle>
                <Line label="Begin time">{formatDate(driver_tran.departure_begin_time)}</Line>
                <Line label="End time">{formatDate(driver_tran.departure_end_time)}</Line>
                <Line label="Total" strong><Badge variant="info" size="sm">{driver_tran.total_hours} hours</Badge></Line>
                <Line label="Begin (Km)">{driver_tran.departure_begin_kilometer}</Line>
                <Line label="End (Km)">{driver_tran.departure_end_kilometer}</Line>
                <Line label="Total" strong><Badge variant="info" size="sm">{driver_tran.total_kilometers} km</Badge></Line>
                <Line label={<><FaParking className="tw-inline tw-mr-1" /> Parking Fee</>} strong>
                  <Badge variant="info" size="sm">{formatNumber(driver_tran.parking_fee, 'VND')} VND</Badge>
                </Line>

                <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-my-3">Attachments</h6>
                <div>
                  {(driver_tran?.images || []).map((attachment) => (
                    <DocumentAttachment
                      {...attachment}
                      key={attachment.id}
                      isTable
                      allowView
                      allowRemove={allow_remove_image}
                      allowDownload
                      handleRemoveAttachment={(imageId) => removeDriverTranDocument(imageId)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <TransactionDetailBookingInfo transaction={transaction} />
    </div>
  );
};

export default TransactionDriverUpdateDeparture;
